import React, { ChangeEvent, InputHTMLAttributes, ReactNode } from 'react';

import Link from 'next/link';

import TextSmall from '@/components/Typography/TextSmall';
import cn from '@/helpers/utils/classNames';

interface RawCheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'children' | 'type' | 'onChange' | 'id' | 'value' | 'name'> {
  children: ReactNode;
  inputClassName?: string;
  containerClassName?: string;
  childrenClassName?: string;
  id?: string | number;
  value?: boolean;
  name?: string;
  error?: string;
  errorClassName?: string;
  onChange?: (value: boolean) => void;
}

type CheckboxProps = RawCheckboxProps &
  (
    | {
        as?: 'Link';
        href: string;
      }
    | {
        as?: 'checkbox';
      }
  );

const Checkbox = ({
  children,
  inputClassName,
  containerClassName,
  childrenClassName,
  id,
  error,
  errorClassName,
  onChange,
  value,
  name,
  as,
  ...rest
}: CheckboxProps) => {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange && onChange(event.target.checked);
  };

  const isChecked = !!value;

  return (
    <div>
      <div className={cn('v2-flex v2-w-auto v2-items-center', containerClassName)}>
        {as === 'Link' && (rest as unknown as { href: string }).href ? (
          <Link
            href={(rest as unknown as { href: string }).href}
            className={cn(`v2-peer/radio-btn v2-radio-btn v2-appearance-none`)}
          >
            <span
              className={cn(
                'v2-inline-block',
                'v2-accent-primary-600 v2-relative v2-mr-2 v2-h-4 v2-rounded-xs v2-min-h-[1rem] v2-w-4 v2-min-w-[1rem]',
                'v2-border-gray-300 v2-appearance-none v2-border',
                'before:pointer-events-none before:absolute before:h-4 before:w-4 before:scale-0 before:bg-transparent before:opacity-0',
                "after:absolute after:z-[1] after:block after:h-4 after:w-4 after:content-[''] after:[transform:translate(-50%,-50%)]",
                'hover:cursor-pointer hover:shadow-none hover:outline-none hover:ring-0  disabled:pointer-events-none',
                {
                  "v2-bg-primary-600 after:bg-checkbox-tick after:v2-text-[12px] after:v2-text-white border-primary before:opacity-[0.16] after:absolute after:left-[6px] after:top-0.5 after:h-[7px] after:w-[7px] after:-translate-x-1/2 after:-translate-y-1/2 after:border-primary after:content-['✔']  focus:border-primary":
                    isChecked,
                  'border !border-red-500 focus:!border-red-500 focus:!shadow-none': error,
                },
                inputClassName
              )}
            ></span>
            {children}
          </Link>
        ) : (
          <>
            <input
              id={id?.toString()}
              name={name}
              className={cn(
                'v2-accent-primary-600 v2-relative v2-mr-2 v2-h-4 v2-rounded-xs v2-min-h-[1rem] v2-w-4 v2-min-w-[1rem]',
                'v2-border-gray-300 v2-appearance-none v2-border',
                'before:pointer-events-none before:absolute before:h-4 before:w-4 before:scale-0 before:bg-transparent before:opacity-0',
                "after:absolute after:z-[1] after:block after:h-4 after:w-4 after:content-[''] after:[transform:translate(-50%,-50%)]",
                `checked:v2-bg-primary-600 checked:after:bg-checkbox-tick checked:after:v2-text-[12px] checked:after:v2-text-white checked:border-primary checked:before:opacity-[0.16] checked:after:absolute checked:after:left-[6px] checked:after:top-0.5 checked:after:h-[7px] checked:after:w-[7px] checked:after:-translate-x-1/2 checked:after:-translate-y-1/2 checked:after:border-primary  checked:after:content-['']`,
                'hover:cursor-pointer hover:shadow-none hover:outline-none hover:ring-0 checked:focus:border-primary disabled:pointer-events-none',
                inputClassName,
                {
                  'border !border-red-500 focus:!border-red-500 focus:!shadow-none': error,
                }
              )}
              type="checkbox"
              value={value ? '1' : '0'}
              checked={value}
              onChange={handleChange}
              {...rest}
            />
            <label className={cn('cursor-pointer', childrenClassName)} htmlFor={id?.toString()}>
              {children}
            </label>
          </>
        )}
      </div>
      {error && (
        <TextSmall variant="danger" className={errorClassName}>
          {error}
        </TextSmall>
      )}
    </div>
  );
};

export default Checkbox;
